import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Lightning, List, X, GithubLogo } from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Tools" },
  { to: "/about", label: "About" },
];

const Header = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full border-b border-border/70 backdrop-blur-xl bg-background/70"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link
            to="/"
            data-testid="brand-logo"
            className="group flex items-center gap-2.5"
          >
            <span className="grid h-9 w-9 place-items-center bg-primary text-primary-foreground transition-transform duration-150 group-hover:-translate-y-0.5">
              <Lightning weight="fill" size={20} />
            </span>
            <span className="font-display text-xl font-extrabold tracking-tighter">
              Expert<span className="text-primary">&nbsp;Technologies</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`text-sm font-semibold tracking-tight transition-colors hover:text-primary ${
                  location.pathname === item.to
                    ? "text-foreground"
                    : "text-muted-foreground"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <a
              href="https://github.com"
              target="_blank"
              rel="noreferrer"
              data-testid="nav-github"
              className="flex items-center gap-2 border border-border bg-card px-3.5 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-all duration-150 hover:-translate-y-0.5 hover:border-primary hover:text-primary"
            >
              <GithubLogo size={16} weight="bold" /> Star
            </a>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="grid h-10 w-10 place-items-center border border-border md:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <List size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border/70 bg-background md:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col px-5 py-3">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className="py-3 text-sm font-semibold tracking-tight"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="mt-24 border-t border-border/70 bg-card"
  >
    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
      <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
        <div className="max-w-sm">
          <div className="flex items-center gap-2.5">
            <span className="grid h-8 w-8 place-items-center bg-primary text-primary-foreground">
              <Lightning weight="fill" size={18} />
            </span>
            <span className="font-display text-lg font-extrabold tracking-tighter">
              Expert<span className="text-primary">&nbsp;Technologies</span>
            </span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            A precision workbench of free online tools for SEO, text and the
            web. No logins. No limits. Everything runs instantly in your
            browser.
          </p>
        </div>
        <div className="flex gap-16">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
              Product
            </p>
            <div className="mt-4 flex flex-col gap-3 text-sm">
              <Link to="/" className="hover:text-primary">All Tools</Link>
              <Link to="/about" className="hover:text-primary">About</Link>
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
              Legal
            </p>
            <div className="mt-4 flex flex-col gap-3 text-sm text-muted-foreground">
              <span>Privacy</span>
              <span>Terms</span>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-12 border-t border-border/70 pt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-mono text-xs text-muted-foreground">
          © {new Date().getFullYear()} Expert Technologies — built for makers.
        </p>
        <p className="font-mono text-xs text-muted-foreground">
          Powered by{" "}
          <span className="font-bold text-primary">
            LeadTech Connect Private Limited
          </span>
        </p>
      </div>
    </div>
  </footer>
);

export default function Layout({ children }) {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
